import { memo, useRef } from 'react';
import clsx from 'clsx';

import {
  useAnimatedMounting,
  useEventListener,
  useOnClickOutside,
} from 'shared/lib/utils';
import { MODAL_ANIMATION_DURATION } from 'shared/config/constants';
import { Modal } from 'shared/ui/modal';
import { PlyrVideoUtils } from 'shared/lib/plyr-video-utils';
import { PlyrVideoPlayer } from 'shared/ui/plyr-video-player';

import { VideoPlayerModalProps } from './types';
import styles from './styles.module.scss';

const VideoModal = ({
  onClose,
  videoSrc,
  posterSrc,
  captionsSrc,
}: VideoPlayerModalProps) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const { isMounted, handleAnimatedUnmount } = useAnimatedMounting(
    onClose,
    MODAL_ANIMATION_DURATION,
  );

  const sourceInfo = PlyrVideoUtils.generateSourceInfo({
    videoSrc,
    posterSrc,
    captionsSrc,
  });

  const handleModalKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;

    handleAnimatedUnmount();
  };

  useOnClickOutside(modalContentRef, handleAnimatedUnmount);

  useEventListener('keydown', handleModalKeydown);

  return (
    <Modal
      isVisible={isMounted}
      accessibilityAttributes={{
        'aria-labelledby': 'recommendation-video-player',
      }}
      modalClassnames={styles.modal}
      loopedTabulationSelectors="button, input"
      excludeIntercomButton
    >
      <div
        className={clsx('layout-context-compact', styles.content)}
        id="recommendation-video-player"
        aria-label="Educator Home video player"
        ref={modalContentRef}
      >
        <button
          onClick={handleAnimatedUnmount}
          className={clsx('btn', 'btn-icon-ghost', styles.closeButton)}
          aria-label="Close video modal"
          data-testid="close-panel-btn"
        >
          <i className="icon-v2-close" aria-hidden />
        </button>
        <PlyrVideoPlayer
          source={sourceInfo}
          containerClassnames={styles.playerContainer}
        />
      </div>
    </Modal>
  );
};

const MemoizedVideoModal = memo(VideoModal);

export default MemoizedVideoModal;
